import { createFileRoute } from "@tanstack/react-router";
import z from "zod";
import { newsListQueryOptions } from "@/lib/client/apex-news";
import { seo } from "@/lib/utils/seo";
import { getNewsListCanonicalUrl, NEWS_LIST_PAGE_SIZE } from "@/features/news/list/constants";
import { NewsListPage } from "@/features/news/list/news-list-page";

export const Route = createFileRoute("/_index/news/")({
  component: RouteComponent,
  staticData: {
    title: "新闻",
  },
  validateSearch: z.object({
    page: z.coerce.number().int().min(1).catch(1).default(1),
    source: z.enum(["all", "ea", "x"]).catch("all").default("all"),
  }),
  loaderDeps: ({ search }) => search,
  async loader({ context, deps }) {
    const offset = (deps.page - 1) * NEWS_LIST_PAGE_SIZE;
    const data = await context.queryClient.ensureQueryData(
      newsListQueryOptions({ offset, limit: NEWS_LIST_PAGE_SIZE, source: deps.source }),
    );
    return {
      ...data,
      page: deps.page,
      source: deps.source,
    };
  },
  head: async ({ loaderData }) => {
    const data = await Promise.resolve(
      loaderData as
        | {
            page: number;
            source: "all" | "ea" | "x";
            total: number;
          }
        | undefined,
    );

    const page = Math.max(1, data?.page ?? 1);
    const source = data?.source ?? "all";
    const totalPages = Math.max(1, Math.ceil((data?.total || 0) / NEWS_LIST_PAGE_SIZE));
    const canonical = getNewsListCanonicalUrl(page, source);
    const links = [];

    if (page > 1) {
      links.push({ rel: "prev", href: getNewsListCanonicalUrl(page - 1, source) });
    }

    if (page < totalPages) {
      links.push({ rel: "next", href: getNewsListCanonicalUrl(page + 1, source) });
    }

    return {
      meta: [
        ...seo({
          title: "Apex 新闻中心 | CN Apex Club",
          description: "查看最新 Apex Legends 新闻、更新公告与活动动态。",
          keywords: "Apex 新闻,APEX 更新,活动公告,补丁说明",
          url: canonical,
          canonical,
          type: "website",
        }),
      ],
      links,
    };
  },
});

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const page = search.page;
  const source = search.source;

  return (
    <NewsListPage
      page={page}
      source={source}
      onPageChange={(nextPage) =>
        navigate({
          search: (prev: { page: number; source: "all" | "ea" | "x" }) => ({
            ...prev,
            page: nextPage,
          }),
          replace: true,
        })
      }
      onSourceChange={(nextSource) =>
        navigate({
          search: () => ({
            page: 1,
            source: nextSource,
          }),
          replace: true,
        })
      }
    />
  );
}
